import { MEDCALL_OPERATOR_REPORT_DEFINITIONS, MEDCALL_MONITORING_REPORT_DEFINITIONS, type MedcallOperatorReportItem, type MedcallParticipantMedication } from '@/pages/medcall/MedcallOperatorReportsMenu';
import type { ProfileValue } from './types';
import { profileReportParticipant } from './profileMedcallReport';
import { medcallPresentation } from './medcallPresentation';

interface PopulationRow { key: string; timestamp: string | null; values: ProfileValue[] }
export interface PopulationClient {
  odrnumber: number; participant: number; timestamp: string | null; staff: string | null; facility: string | null;
  identity: ProfileValue[] | null; medications: (PopulationRow & { timelines: PopulationRow[] })[] | null; statuses: PopulationRow[] | null;
}
export interface PopulationPage { items: PopulationClient[] | null; hasMore: boolean | null; next: number | null }
export interface PopulationFilters { programs: string[]; providers: string[]; facilities: number[]; nurse: string }
interface ClinicalMedication extends MedcallParticipantMedication {
  key: string; active: boolean; prn: boolean; timestamp: string | null;
  timelines: { date: string | null; status: string | null; created_at: string | null }[];
}
interface ClinicalClient extends MedcallOperatorReportItem { rawFacility: string; facilityId: number; medications: ClinicalMedication[]; timestamp: string | null }
const rules = medcallPresentation.reports;
const map = (values: ProfileValue[]) => Object.fromEntries(values.map(field => [field.name, field.value]));
const objects = (stored: string | null | undefined): Record<string, unknown>[] => {
  if (!stored) return [];
  let parsed: unknown;
  try { parsed = JSON.parse(stored); } catch { throw new Error('A clinical value needs review before reporting.'); }
  if (!Array.isArray(parsed) || parsed.some(value => !value || typeof value !== 'object' || Array.isArray(value))) throw new Error('A clinical value needs review before reporting.');
  return parsed as Record<string, unknown>[];
};
const string = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value !== 'string') throw new Error('A clinical value needs review before reporting.');
  return value;
};
const person = (stored: string | null | undefined) => {
  if (!stored) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(stored); } catch { throw new Error('A clinical value needs review before reporting.'); }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('A clinical value needs review before reporting.');
  const value = parsed as Record<string, unknown>;
  return { firstname: string(value.firstname), lastname: string(value.lastname), title: string(value.title) };
};
const flag = (value: string | null | undefined) => {
  if (value == null || value === '0') return false;
  if (value === '1') return true;
  throw new Error('A clinical value needs review before reporting.');
};
const date = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(Date.parse(`${value}T00:00:00Z`)) || new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) !== value) throw new Error('Select valid MedCall report dates.');
  return value;
};
const addDays = (value: string, days: number) => new Date(Date.parse(`${date(value)}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
const monthOffset = (value: string, months: number) => {
  const [year, month, day] = date(value).split('-').map(Number);
  const end = new Date(Date.UTC(year, month + months, 0)).getUTCDate();
  return new Date(Date.UTC(year, month - 1 + months, Math.min(day, end))).toISOString().slice(0, 10);
};
const inRange = (value: string | null | undefined, from: string, to: string) => Boolean(value && value >= from && value <= to);

export const profilePopulationClient = (client: PopulationClient): ClinicalClient => {
  const clinical = profileReportParticipant({ ...client, from: null, to: null });
  const values = map(client.identity!);
  const medications = client.medications!.map((row, index): ClinicalMedication => {
    const values = map(row.values); const medication = clinical.medications![index];
    return { id: index + 1, key: row.key, timestamp: row.timestamp, active: Boolean(medication.active), prn: Boolean(medication.prn),
      medicine_name: values.medicine_name, strength: values.strength, route: values.route, frequency: values.frequency, times_per_day: values.times_per_day,
      refills: objects(values.refills).map(value => ({ refill_date: string(value.refill_date) })),
      injection_info: objects(values.injection_info).map(value => {
        const onSite = value.on_site == null ? null : Number(value.on_site);
        if (onSite != null && (!Number.isInteger(onSite) || ![0, 1, 2].includes(onSite))) throw new Error('A clinical value needs review before reporting.');
        return { date_due: string(value.date_due), date_given: string(value.date_given), date_injection_arrived: string(value.date_injection_arrived), on_site: onSite };
      }),
      timelines: row.timelines.map(timeline => { const values = map(timeline.values); return { date: values.date, status: values.status, created_at: values.created_at }; })
        .sort((left, right) => (right.date ?? '').localeCompare(left.date ?? '')),
    };
  });
  return { id: client.odrnumber, participant_id: client.participant, firstname: values.firstname, lastname: values.lastname, dob: values.dob,
    cid: values.cid, program: values.program, provider: values.provider, facility: client.facility ?? values.facility,
    rawFacility: values.facility ?? '', facilityId: Number(values.option_facility_id), timestamp: client.timestamp,
    staff_assigned: client.staff, nurse_name: person(values.nurse_name), psychiatrist: person(values.psychiatrist), active: flag(values.active),
    aliases: objects(values.aliases).map(alias => ({ firstname: string(alias.firstname), lastname: string(alias.lastname) })),
    activation_history: objects(values.activation_history).map(value => ({ fullname: string(value.fullname) })), medications,
  };
};

export const populationReportScope = (kind: string, scope: 'all' | 'my') => rules.allClientReports.includes(kind) ? 'all' : scope;
export const populationReportRange = (kind: string, today: string, from?: string, to?: string) => {
  const end = date(to || today);
  if (kind === 'report-out-of-compliance') {
    const [year, month, day] = end.split('-').map(Number);
    const start = from ? date(from) : new Date(Date.UTC(year, month - 2, day)).toISOString().slice(0, 10);
    if (start > end) throw new Error('Select a start date on or before the end date.');
    return { from: start > addDays(end, -6) ? addDays(end, -6) : start, to: end };
  }
  if (kind === 'report-ih-houses' || kind === 'report-taken-not-taken') {
    if (!from || !to || date(from) > end) throw new Error('Select a start date on or before the end date.');
    if ((Date.parse(end) - Date.parse(from)) / 86400000 > 365) throw new Error('Select a MedCall report range of at most 366 dates.');
    return { from, to: end };
  }
  return { from: from ? date(from) : addDays(end, -30), to: end };
};

const compliance = (clients: ClinicalClient[], from: string, to: string, today: string) => {
  const items: MedcallOperatorReportItem[] = []; let totalCount = 0;
  for (const client of clients.filter(client => client.active && !rules.monitoringExcludedPrograms.includes(client.program ?? ''))) {
    let updated = client.timestamp?.slice(0, 10) ?? ''; let tracked = 0;
    const out: (ClinicalMedication & { last_taken_date: string | null })[] = [];
    for (const medication of client.medications.filter(medication => medication.active && !medication.prn && medication.route !== 'IM-LAI')) {
      const timelines = medication.timelines.filter(row => !['refill', 'injection'].includes(row.status ?? '') && inRange(row.date, from, to));
      if (!timelines.length) continue;
      tracked += 1;
      const taken = timelines.find(row => row.status === 'taken')?.date;
      const priorTaken = medication.timelines.find(row => row.status === 'taken')?.date;
      const lastTaken = taken || priorTaken || null;
      updated = [updated, medication.timestamp?.slice(0, 10) ?? '', lastTaken || today].sort().slice(-1)[0];
      if (!taken || taken < addDays(timelines[0].date!, -5)) out.push({ ...medication, last_taken_date: lastTaken });
    }
    if (!tracked) continue;
    totalCount += 1;
    if (!updated || updated >= addDays(to, -5)) continue;
    const takenDates = out.map(medication => medication.last_taken_date).filter((value): value is string => Boolean(value)).sort();
    items.push({ ...client, last_updated: updated, last_taken_date: takenDates.slice(-1)[0] ?? null, medications: out });
  }
  return { items: items.map(item => ({ ...item, report_total_count: totalCount })), totalCount };
};

export const buildProfilePopulationReport = (population: PopulationClient[], kind: string, filters: PopulationFilters, today: string, from?: string, to?: string) => {
  if (![...MEDCALL_OPERATOR_REPORT_DEFINITIONS, ...MEDCALL_MONITORING_REPORT_DEFINITIONS].some(report => report.key === kind)) throw new Error('Select a MedCall report.');
  const range = populationReportRange(kind, today, from, to);
  const clients = population.map(profilePopulationClient).filter(client =>
    (!filters.programs.length || filters.programs.includes(client.program ?? '')) && (!filters.providers.length || filters.providers.includes(client.provider ?? ''))
    && (!filters.facilities.length || filters.facilities.includes(client.facilityId))
    && (!filters.nurse || [client.nurse_name?.firstname, client.nurse_name?.lastname, client.nurse_name?.title].filter(Boolean).join(' ').toLowerCase().includes(filters.nurse.toLowerCase())));
  if (kind === 'report-out-of-compliance') return { ...compliance(clients, range.from, range.to, today), ...range };
  if (kind === 'report-whole-population' || kind === 'report-scattered-erc') {
    const items = clients.filter(client => kind === 'report-whole-population' || (client.active && rules.scatteredPrograms.includes(client.program ?? ''))).map(client => {
      const active = client.medications.filter(medication => medication.active); const names = (rows: ClinicalMedication[]) => rows.map(row => row.medicine_name ?? '');
      return { ...client, active_medications_count: client.active ? active.length : 0, has_active_medications: Boolean(active.length), active_medication_names: names(active),
        lai_medication_names: names(active.filter(medication => medication.route === 'IM-LAI')),
        buprenorphine_medication_names: names(active.filter(medication => /(buprenorphine|methadone|naltrexone|vivitrol)/i.test(medication.medicine_name ?? ''))),
        prn_medication_names: names(active.filter(medication => medication.prn)) };
    });
    return { items, totalCount: items.length, ...range };
  }
  if (kind === 'report-lai-30-days') {
    const older = `${monthOffset(today, -2)} 00:00:00`; const recent = `${monthOffset(today, -1)} 23:59:59`;
    const items = clients.filter(client => client.active && client.medications.some(medication => {
      if (medication.route !== 'IM-LAI') return false;
      if (medication.timelines.some(row => !row.created_at)) throw new Error('A dose creation date needs review before the LAI report.');
      return medication.timelines.some(row => row.created_at! >= older && row.created_at! <= recent) && !medication.timelines.some(row => row.created_at! >= recent);
    }));
    return { items, totalCount: items.length, ...range };
  }
  if (kind === 'report-ih-houses') {
    const houses = new Map<string, MedcallOperatorReportItem>();
    for (const client of clients.filter(client => client.active && rules.housingFacilityTerms.some(term => client.rawFacility.includes(term)))) {
      if (!client.facility) continue;
      const timelines = client.medications.filter(medication => !medication.prn).flatMap(medication => medication.timelines).filter(row => row.status && inRange(row.date, range.from, range.to));
      if (!timelines.length) continue;
      const house = houses.get(client.facility) ?? { id: 0, facility: client.facility, start_date: range.from, end_date: range.to, participants: [] };
      house.participants!.push({ participant_id: client.participant_id, firstname: client.firstname, lastname: client.lastname, dob: client.dob,
        adherence: Math.ceil(100 * timelines.filter(row => row.status === 'taken').length / timelines.length) });
      houses.set(client.facility, house);
    }
    const items = Array.from(houses.values()).map(house => ({ ...house, adherence: Math.ceil(house.participants!.reduce((sum, row) => sum + (row.adherence ?? 0), 0) / house.participants!.length) }));
    return { items, totalCount: items.length, ...range };
  }
  const items: MedcallOperatorReportItem[] = [];
  for (const client of clients.filter(client => client.active && !rules.medicationExcludedPrograms.includes(client.program ?? ''))) {
    const medications = client.medications.filter(medication => medication.active).filter(medication => {
      if (kind === 'report-taken-not-taken') return medication.route !== 'IM-LAI';
      if (kind.startsWith('report-run-out')) {
        const refill = medication.refills?.[0]?.refill_date;
        if (medication.route === 'IM-LAI' || !refill) return false;
        if (kind === 'report-run-out') return refill < today;
        return inRange(refill, today, addDays(today, kind === 'report-run-out-3-days' ? 3 : 7));
      }
      const injection = medication.injection_info?.[0];
      if (medication.route !== 'IM-LAI' || !injection?.date_due) return false;
      if (kind === 'report-injections') return injection.date_due < today && !injection.date_given;
      return inRange(injection.date_due, today, addDays(today, kind === 'report-inj-due-3-days' ? 3 : 7));
    }).map(medication => {
      if (kind !== 'report-taken-not-taken') return medication;
      const current = population.find(row => row.odrnumber === client.id)!;
      const statuses = new Map(current.statuses!.map(row => { const values = map(row.values); return [values.date, values.status]; }));
      const timelines = rules.projectStatusPrograms.includes(client.program ?? '')
        ? Array.from({ length: (Date.parse(range.to) - Date.parse(range.from)) / 86400000 + 1 }, (_, index) => {
          const day = addDays(range.from, index); return { date: day, status: statuses.get(day) || 'notTaken' };
        }) : medication.timelines.filter(row => !['refill', 'injection'].includes(row.status ?? '') && inRange(row.date, range.from, range.to)).sort((left, right) => (left.date ?? '').localeCompare(right.date ?? ''));
      return { ...medication, timelines };
    });
    if (medications.length) items.push({ ...client, medications });
  }
  return { items, totalCount: items.length, ...range };
};
